import { Box, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, useColorModeValue, useToast, VStack } from "@chakra-ui/react";
import { useMemo, useRef, useState } from "react";

import { $length, $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { G1Icon } from "@/utils/icons";

import { ModalWindowState } from "./ModalConstants";
import { localtrans } from "@/utils/LocalTranslations";
import { defaultInfoToast } from "@/utils/toast";

interface SignatureModalState extends ModalWindowState {
    isOpen: boolean;
    emailToConfirm?: string;
    onQualifiedSigning: () => void;
    onSendOTP: (email: string) => Promise<boolean>;
    onSignWithOTP: (otp: string) => Promise<Nullable<string>>;
}

export const SignatureModal = (props: SignatureModalState) => {
    const { isOpen, emailToConfirm, onClose, onQualifiedSigning, onSendOTP, onSignWithOTP } = props;
    const [step, setStep] = useState<SignatureStep>(SignatureStep.SELECT_TYPE);
    const [email, setEmail] = useState("");
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [displaySpinner, setDisplaySpinner] = useState("none");
    const [isVerifying, setIsVerifying] = useState(false);
    const [signStep, setSignStep] = useState("");
    const maskedEmail = useMemo(() => {
        if (!$ok(emailToConfirm)) { return ""; }
        const index = emailToConfirm!.indexOf('@');
        return (index > 5) ? `${emailToConfirm!.substring(0, index-5)}*****@${emailToConfirm!.substring(index)}` : `*****@${emailToConfirm!.substring(index)}`;
    }, [emailToConfirm]);
    const inputRefs = useRef<Nullable<HTMLInputElement>[]>([]);
    const toast = useToast();
    const otpBorderColor = useColorModeValue("blue.400", "blue.100");
    const otpBgColor = useColorModeValue("blue.50", "blue.400");
    const qualifiedBorderColor = useColorModeValue("green.400", "green.50");
    const qualifiedBgColor = useColorModeValue("green.50", "green.400");
    const textColor = useColorModeValue("gray.600", "gray.400");

    const resetModal = () => {
        setStep(SignatureStep.SELECT_TYPE);
        setEmail('');
        setOtpValues(['', '', '', '', '', '']);
        setDisplaySpinner("none");
        setIsVerifying(false);
        setSignStep("");
    };

    const signWithOTP = async (otp: string) => {
        setIsVerifying(true);
        setSignStep(localtrans("SignatureModal.SigningDocStep"));
        setDisplaySpinner("flex");
        const ret = await onSignWithOTP(otp);
        if (ret === null) {
            handleClose();
        } else {
            setDisplaySpinner("none");
            setIsVerifying(false);
            setSignStep(ret!);
        }
    }

    const handleClose = async () => {
        resetModal();
        await onClose();
    };

    const handleSignatureChoice = async (step: SignatureStep) => {
        setStep(step);
        if (step === SignatureStep.QUALIFIED) {
            await handleClose();
            onQualifiedSigning();
        }
    }

    const handleEmailConfirmation = async () => {
        const success = await onSendOTP(email);
        if (success) { setStep(SignatureStep.INPUT_OTP); }
    };

    const backToChoice = () => {
        setStep(SignatureStep.SELECT_TYPE);
        setEmail("");
    }

    const handleOTPChange = async (index: number, value: string) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        if ($length(numericValue) <= 1) {
            const newOtpValues = [...otpValues];
            newOtpValues[index] = numericValue;
            setOtpValues(newOtpValues);

            if (numericValue && index < 5) {
                inputRefs.current[index + 1]?.focus();
            } else if (numericValue && index === 5) {
                const completeOtp = [...newOtpValues.slice(0, 5), numericValue].join('');
                await signWithOTP(completeOtp);
            }
        }
    }

    const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }

    const handleOTPPaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
        if ($length(pastedData) === 6) {
            const newOtpValues = pastedData.split('');
            setOtpValues(newOtpValues);
            inputRefs.current[5]?.focus();
            await signWithOTP(pastedData);
        }
    }

    const handleResendOTP = async () => {
        setSignStep("");
        setDisplaySpinner("none");
        setOtpValues(['', '', '', '', '', '']);
        const success = await onSendOTP(email);
        if (success) {
            toast(defaultInfoToast(`Un nouveau code OTP a été envoyé à l'adresse ${email}`));
        }
    }

    const selectSignatureTypeElement = (
        <HStack spacing={4} align="stretch">
            <Box as="button" p={5} flex="1" borderWidth="2px" borderRadius="lg" borderColor="gray.200" _hover={{ borderColor: otpBorderColor, bg: otpBgColor }} transition="all 0.2s"
                 onClick={() => handleSignatureChoice(SignatureStep.CONFIRM_EMAIL)}>
                <HStack justifyContent="center">
                    <G1Icon.MailOTP />
                    <Text fontSize="lg" fontWeight="bold" mb={2}>{localtrans("SignatureModal.UsedEmailOTP")}</Text>
                </HStack>
                <Text fontSize="sm" color={textColor}>{localtrans("SignatureModal.UsedEmailOTPDesc")}</Text>
            </Box>

            <Box as="button" p={5} flex="1" borderWidth="2px" borderRadius="lg" borderColor="gray.200" _hover={{ borderColor: qualifiedBorderColor, bg: qualifiedBgColor }} transition="all 0.2s"
                 onClick={() => handleSignatureChoice(SignatureStep.QUALIFIED)}>
                <HStack justifyContent="center">
                    <G1Icon.Qualified />
                    <Text fontSize="lg" fontWeight="bold" mb={2}>{localtrans("SignatureModal.UsedQualifiedType")}</Text>
                </HStack>
                <Text fontSize="sm" color={textColor}>{localtrans("SignatureModal.UsedQualifiedTypeDesc")}</Text>
            </Box>
        </HStack>
    );

    const confirmEmailElement = (
        <VStack spacing={4} align="stretch">
            <G1Input component={"form"} type="email" placeholder={maskedEmail} value={email} size="md" onChange={(e) => setEmail(e.target.value)} />
            <Text fontSize="sm" color={textColor}>{localtrans("SignatureModal.ConfirmEmailDesc")}</Text>
        </VStack>
    );

    const inputOTPElement = (
        <VStack spacing={4} align="stretch">
            <Text color={textColor} textAlign="center" fontSize="md">
                {localtrans("SignatureModal.SentCode")}
                <br/>
                <Text as="span" fontWeight="bold">{email}</Text>
            </Text>

            <Box display="flex" justifyContent="center" gap={2}>
                {otpValues.map((value, index) => (
                    <Input key={index} type="text" ref={(el) => (inputRefs.current[index] = el)} inputMode="numeric" maxLength={1} value={value} disabled={isVerifying}
                           textAlign="center" fontSize="2xl" fontWeight="bold" w="50px" h="60px" borderWidth="2px" borderColor={value ? 'blue.400' : 'gray.200'}
                           _focus={{
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                           }}
                           onChange={(e) => handleOTPChange(index, e.target.value)}
                           onKeyDown={(e) => handleOTPKeyDown(index, e)}
                           onPaste={index === 0 ? (e) => handleOTPPaste(e) : undefined}
                    />
                  ))}
            </Box>
            <Box justifyContent="center">
                <Text fontSize="sm" fontStyle="italic" color="red" textAlign="center" mr={2}>{signStep}</Text><Spinner display={displaySpinner} size="xs"speed="1s" />
            </Box>
            <Text fontSize="sm" color={textColor} textAlign="center">
                {localtrans("SignatureModal.NotReceivedOTP")}
                <Text as="button" color="blue.500" fontWeight="semibold" _hover={{ textDecoration: 'underline' }} disabled={isVerifying} onClick={handleResendOTP}>
                    {localtrans("SignatureModal.ResendOTP")}
                </Text>
            </Text>
        </VStack>
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    { step === SignatureStep.SELECT_TYPE && localtrans("SignatureModal.ChoiceSignatureType") }
                    { step === SignatureStep.CONFIRM_EMAIL && localtrans("SignatureModal.ConfirmEmail") }
                    { step === SignatureStep.INPUT_OTP && localtrans("SignatureModal.ConfirmOTP") }
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={6}>
                    { step === SignatureStep.SELECT_TYPE && selectSignatureTypeElement }
                    { step === SignatureStep.CONFIRM_EMAIL && confirmEmailElement }
                    { step === SignatureStep.INPUT_OTP && inputOTPElement }
                </ModalBody>

                {step === SignatureStep.CONFIRM_EMAIL && (
                    <ModalFooter>
                        <G1Button component="form" variant="cancel" title={localtrans("back")} mr={3} onClick={backToChoice} />
                        <G1Button component="form" variant="ok" title={localtrans("confirm")} isDisabled={email !== emailToConfirm} onClick={handleEmailConfirmation} />
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
} ;

enum SignatureStep {
    SELECT_TYPE = 1,
    CONFIRM_EMAIL = 2,
    INPUT_OTP = 3,
    QUALIFIED = 4
}